import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

const AppContext = createContext(null);

const BRAND = { brand_name: "Bizlinkk", tagline: "Your Business. Your Website. One Simple Link." };

export function AppProvider({ children }) {
    const [user, setUser] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const settings = BRAND;
    const { setLang } = useI18n();

    const applyUserLang = useCallback((u) => {
        const code = u?.language;
        if (code) {
            const stored = localStorage.getItem("vyapaar_lang");
            if (code !== stored) setLang(code);
        }
    }, [setLang]);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            applyUserLang(data);
        } catch {
            setUser(null);
        } finally {
            setAuthChecked(true);
        }
    }, [applyUserLang]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data?.access_token) localStorage.setItem("vyapaar_token", data.access_token);
        setUser(data.user);
        applyUserLang(data.user);
        return data;
    };

    const applyAuthData = useCallback((data) => {
        if (data?.access_token) localStorage.setItem("vyapaar_token", data.access_token);
        setUser(data.user);
        setAuthChecked(true);
        applyUserLang(data.user);
        return data;
    }, [applyUserLang]);

    const googleSession = useCallback(async (sessionId) => {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        return applyAuthData(data);
    }, [applyAuthData]);

    const register = async (name, email, password) => {
        const { data } = await api.post("/auth/register", { name, email, password });
        if (data?.access_token) localStorage.setItem("vyapaar_token", data.access_token);
        setUser(data.user);
        applyUserLang(data.user);
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("vyapaar_token");
        setUser(null);
    };

    useEffect(() => {
        // CRITICAL: If returning from Google OAuth, skip the /me check.
        // AuthCallback exchanges the session_id and establishes the session first.
        if (window.location.hash?.includes("session_id=")) { setAuthChecked(true); return; }
        refreshMe();
    }, [refreshMe]);

    return (
        <AppContext.Provider value={{ user, authChecked, login, register, logout, refreshMe, applyAuthData, googleSession, settings }}>
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used within AppProvider");
    return ctx;
}
